import { useWriteContract, useWaitForTransactionReceipt } from 'wagmi'
import { parseUnits } from 'viem'
import { BRIDGE_CONTRACT_ADDRESS, UNI2_CHAIN_ID } from '@/constants/addresses'
import { BRIDGE_ABI } from '@/constants/abis'

interface LockParams {
  amount: string // amount to lock (as string, e.g. "10")
  expectedAmountOut: string // amount after fee (as string, e.g. "9.5")
  bscRecipient: string // BSC address as string (e.g. "0x123...")
}

export function useLockTokens() {
  const {
    data: hash,
    writeContract,
    isPending: isWriting,
    error: writeError,
    reset,
  } = useWriteContract()

  const {
    isLoading: isConfirming,
    isSuccess,
    data: receipt,
    error: confirmError,
  } = useWaitForTransactionReceipt({
    hash,
  })

  const lockTokens = async ({ amount, expectedAmountOut, bscRecipient }: LockParams) => {
    const amountWei = parseUnits(amount, 18)
    const expectedAmountOutWei = parseUnits(expectedAmountOut, 18)

    writeContract({
      address: BRIDGE_CONTRACT_ADDRESS,
      abi: BRIDGE_ABI,
      functionName: 'lockTokens',
      args: [amountWei, expectedAmountOutWei, bscRecipient],
      chainId: UNI2_CHAIN_ID,
    })
  }

  // Extract lockId from event if TX was successful
  let lockId: bigint | undefined
  if (receipt?.logs?.[1]) {
    try {
      // TokensLocked event has lockId as first indexed param
      lockId = BigInt(receipt.logs[1].topics[1] || '0')
    } catch {
      // Ignore parse errors
    }
  }

  return {
    lockTokens,
    hash,
    lockId,
    isWriting,
    isConfirming,
    isSuccess,
    isPending: isWriting || isConfirming,
    error: writeError || confirmError,
    reset,
  }
}
